import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useHeader } from '../context/HeaderContext';
import useWebSocket, { generateUUID } from '../utils/useWebSocket';
import Avatar from '../components/Avatar';
import { SendIcon, FlowIcon, CopyIcon, CopyOkIcon, LoadingIcon } from '../icons';
import ReactMarkdown from 'react-markdown';
import normalizeImage, { pickImageLike } from '../utils/image';
import apiClient from '../shared/api/apiClient';

interface Msg {
  id: string;
  message: string;
  is_request: boolean;
  is_loading: boolean;
}

interface IChat {
  uuid: string;
}

const Chat: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const chatId = id || '';
  const { user } = useAuth();
  const { addCrumb, clearCrumb } = useHeader();
  
  const editorRef = useRef<HTMLDivElement>(null);
  const endOfListRef = useRef<HTMLDivElement>(null);
  // Keep last sent text to avoid accidental duplicates
  const lastSentRef = useRef<string>("");
  const [isEmpty, setIsEmpty] = useState(true);
  const [inputText, setInputText] = useState('');
  // Model is optional; when null, backend default (settings.MODEL_ID_FOR_COMPARING) is used
  const [modelId, setModelId] = useState<string | null>(null);
  const [copyId, setCopyId] = useState<string | null>(null);

  // WebSocket connection
  const token = localStorage.getItem('nashezoloto_token') ?? '';
  const wsUrl = useMemo(() => {
    const base = (process.env.REACT_APP_WS_URL || 'ws://localhost:8000').replace(/\/$/, '');
    // If base already ends with /ws, don't append it again
    const hasWs = /\/(ws|websocket)\/?$/.test(base);
    const root = hasWs ? base : `${base}/ws`;
    return `${root}/${chatId}?token=${token}`;
  }, [chatId, token]);
  // Не сохраняем историю в session/localStorage — работаем только с текущим сообщением
  const { messages, sendMessage, addMessage, replaceMessages, close } = useWebSocket(wsUrl);

  useEffect(() => {
    console.log('changed uuid:', id);
    
    // Clear state when uuid changes
    setInputText('');
    editorRef.current && (editorRef.current.innerHTML = '');
    // Also clear any previously rendered messages immediately
    replaceMessages([]);
    // modelId is initialized by default state above

    // Set breadcrumbs
    clearCrumb();
    addCrumb({ title: 'Чат', path: '/app' });
    addCrumb({ title: `Чат ${chatId.slice(0, 8)}...`, path: `/app/chat/${chatId}` });

  }, [chatId, addCrumb, clearCrumb]);

  // Fetch chat history from REST API and seed messages
  useEffect(() => {
    let cancelled = false;
    const loadHistory = async () => {
      if (!chatId) return;
      try {
        const raw = await apiClient.getChatHistory(chatId, modelId || undefined);
        if (cancelled || !Array.isArray(raw)) return;

        // Sort by timestamp if available
        const getTs = (it: any): number => {
          const v = it.created_at ?? it.createdAt ?? it.timestamp ?? it.ts ?? it.time ?? it.date;
          if (v == null) return 0;
          // Try numeric first
          const n = Number(v);
          if (!Number.isNaN(n) && Number.isFinite(n)) {
            // Heuristic: if seconds (10 digits), convert to ms
            if (n > 0 && n < 1e12 && n > 1e9) return Math.floor(n * 1000);
            return n;
          }
          const d = new Date(v).getTime();
          return Number.isFinite(d) ? d : 0;
        };
        const hasTs = Array.isArray(raw) && raw.some((it) => getTs(it) !== 0);
        const base = hasTs ? [...raw].sort((a, b) => getTs(a) - getTs(b)) : raw;

        const mapped = base.map((it: any, idx: number) => {
          const role = (it.role || it.sender || it.author || '').toString().toLowerCase();
          const is_request = typeof it.is_request === 'boolean'
            ? it.is_request
            : role
              ? !(role === 'assistant' || role === 'bot' || role === 'system')
              : Boolean(it.is_user);
          const text = it.message ?? it.content ?? it.text ?? it.body ?? '';
          const id = (it.id || it.message_id || it.uuid || `${chatId}-${idx}-${Date.now()}`).toString();
          return {
            id,
            message: String(text),
            is_request,
            is_loading: false,
          } as Msg;
        });
        replaceMessages(mapped);
      } catch (e) {
        console.warn('Failed to load chat history for', chatId, e);
      }
    };
    loadHistory();
    return () => { cancelled = true; };
  }, [chatId, modelId, replaceMessages]);

  const handleInput = () => {
    if (editorRef.current) {
      const text = editorRef.current.textContent || "";
      setInputText(text);
      setIsEmpty(text.trim() === "");

      if (text.trim() === "") {
        editorRef.current.innerHTML = "";
      }
    }
  };

  const renderSmartSearch = (payload: any) => {
    const items: any[] = Array.isArray(payload?.items) ? payload.items : [];
    if (items.length === 0) return null;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
        {items.map((it, idx) => {
          const title = (it?.langchain_metadata?.name)
            || it.name
            || it.title
            || it.response_name
            || `Товар ${idx + 1}`;
          // Try to extract an image from various shapes: string, object with url/src, or arrays
          const rawImg = pickImageLike(it.image) || pickImageLike(it.images) || pickImageLike(it.thumbnail) || pickImageLike(it.preview);
          const img = normalizeImage(rawImg || undefined);
          const href = it.url || it.href || it.link;
          return (
            <div key={idx} className="flex p-3 rounded-xl border border-gray-200 bg-white">
              {img && (
                <img
                  src={img}
                  alt={title}
                  className="w-16 h-16 object-contain rounded mr-3 flex-shrink-0"
                  onError={(e) => {
                    console.warn('Failed to load image:', img);
                    e.currentTarget.style.display = 'none';
                  }}
                  onLoad={(e) => {
                    e.currentTarget.style.display = 'block';
                  }}
                />
              )}
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate">{title}</div>
                {it.code && (
                  <div className="text-xs text-gray-500 mt-0.5">Код: {it.code}</div>
                )}
                {href && (
                  <div className="mt-1">
                    <a
                      className="text-sm text-brand-700 hover:underline break-words"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Перейти к товару
                    </a>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderResponseMessage = (text: string) => {
    try {
      const parsed = JSON.parse(text);
      if (parsed && parsed.type === 'smart_search_result') {
        return renderSmartSearch(parsed);
      }
    } catch (e) {
      // not JSON – treat as markdown
    }
    return (
      <div className='response-text markdown-body'>
        <ReactMarkdown
          linkTarget="_blank"
          components={{
            // Bold clickable titles/links like in hollyshop
            a: ({node, ...props}) => (
              <a
                {...props}
                rel="noopener noreferrer"
                className="text-brand-700 hover:underline font-semibold"
              />
            ),
            // Center images within the bubble
            img: ({node, ...props}) => {
              const src = normalizeImage((props as any).src || '');
              return (
                <img
                  {...props}
                  src={src}
                  loading="lazy"
                  className="block mx-auto my-3 max-w-full h-auto"
                />
              );
            },
            // Ensure paragraph text is left-aligned
            p: ({node, ...props}) => (
              <p {...props} className={`text-left ${((props as any).className || '')}`.trim()} />
            ),
          }}
        >
          {text}
        </ReactMarkdown>
      </div>
    );
  };

  const handleFocus = () => {
    editorRef.current?.focus();
    setIsEmpty(false);
  };

  const normalizeText = (txt: string) => txt.replace(/\s+/g, ' ').trim();

  const sendClick = () => {
    const raw = editorRef.current?.textContent ?? "";
    const plain = normalizeText(raw);

    if (!plain) return;
    if (!user) return;

    // Do not resend exact same message twice in a row
    if (normalizeText(lastSentRef.current) === plain) {
      return;
    }

    const messageData = {
      id: generateUUID(),
      response_id: generateUUID(),
      branch_id: chatId,
      // Send plain text to backend to avoid HTML artifacts/duplication
      message: plain,
      user_id: user.id,
      // Do not force model on client; let backend default when null
      model_id: modelId || undefined,
    };

    lastSentRef.current = plain;

    sendMessage(JSON.stringify(messageData));
    addMessage({ id: messageData.id, is_request: true, message: messageData.message, is_loading: false });
    addMessage({ id: messageData.response_id, is_request: false, message: "", is_loading: true });

    console.log(messageData);

    if (editorRef.current) {
      editorRef.current.innerHTML = "";
    }
  };

  const handleSendClick = () => {
    sendClick();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !(e.key === 'Enter' && e.shiftKey)) {
      e.preventDefault();
      sendClick();
    }
  };

  const copyClipboard = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopyId(id);
    setTimeout(() => setCopyId(null), 2000);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    const lines = text.split("\n");
    const fragment = document.createDocumentFragment();

    lines.forEach((line, index) => {
      if (index > 0) {
        fragment.appendChild(document.createElement("br"));
      }
      fragment.appendChild(document.createTextNode(line));
    });

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(fragment);

    selection.removeAllRanges();
    selection.addRange(range);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (endOfListRef.current) {
        console.log("New messages received", messages);
        endOfListRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [messages]);

  return (
    <div className='h-full flex flex-col bg-white'>
      {/* Chat messages area */}
      <div className="flex-1 overflow-y-auto pb-6 bg-white">
        <div className={`relative chat-center-wrapper ${messages.length > 0 ? 'py-4' : 'py-8'}`}>
          <div className="chat-container">
          {messages.length === 0 ? (
            <>
              <div className='flex flex-col items-center justify-center min-h-[60vh]'>
                <FlowIcon className='main-flow-svg' />
                <div className='flex flex-col items-center justify-center mt-10'>
                  <p className='hello-main'>Добрый день!</p>
                </div>
                <div className='flex flex-col items-center justify-center mb-8 text-center'>
                  <p className='hello-phrase-main'>
                    Это магазин ювелирных изделий "Наше Золото".<br />
                    Будем рады ответить на ваши вопросы.
                  </p>
                </div>
                
                {/* Model is fixed server-side; selector hidden for end users */}
              </div>
            </>
          ) : (
            <>
              <div className='flex flex-col'>
                <div className='flex flex-col text-sm px-4 messages max-w-4xl mx-auto'>
                  {messages.map((msg) => (
                    <article key={`${msg.id}`} className='mt-10'>
                      {msg.is_request ? (
                        <div className='flex items-start justify-end'>
                          <div className="conversation-turn relative flex w-full min-w-0 flex-col items-end">
                            <div className="flex-col gap-1 md:gap-3">
                              <div className="flex max-w-full flex-col flex-grow">
                                <div className="min-h-8 text-message flex w-full flex-col items-end gap-2 whitespace-normal break-words">
                                  <div className="flex w-full flex-col gap-1 empty:hidden items-end">
                                    <div className="relative bg-white text-gray-800 rounded-tl-xl rounded-tr-xl rounded-bl-xl px-5 py-3 max-w-[620px] shadow-sm">
                                      <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: msg.message }} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex-col ml-3 flex-shrink-0">
                            <div className="align-middle">
                              <Avatar user={user} width={40} height={40} />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className='flex items-start'>
                          <div className="flex-col mr-3 flex-shrink-0">
                            <div className="align-middle" style={{ marginTop: '6px' }}>
                              <div className="h-10 w-10 rounded-full bg-gray-800 text-white flex items-center justify-center text-xs font-semibold">AI</div>
                            </div>
                          </div>
                          <div className="conversation-turn relative flex w-full min-w-0 flex-col">
                            <div className="flex-col gap-1 md:gap-3">
                              <div className="flex max-w-full flex-col flex-grow">
                                <div className="min-h-8 text-message flex w-full flex-col items-start gap-2 whitespace-normal break-words">
                                  <div className="flex w-full flex-col gap-1 empty:hidden items-start">
                                    <div className="relative bg-white border border-gray-200 rounded-xl px-5 py-3 max-w-[620px] shadow-sm group">
                                      {!msg.is_loading ? (
                                        <div className='response-conversation'>
                                          {renderResponseMessage(msg.message)}
                                          <button 
                                            onClick={() => copyClipboard(msg.id, msg.message)}
                                            className="absolute right-2 bottom-2 px-2 py-1 text-sm text-gray-600 bg-white/70 hover:bg-gray-200 rounded shadow-sm inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                            aria-label="Скопировать ответ"
                                          >
                                            {copyId === msg.id ? (
                                              <>
                                                <CopyOkIcon className='copy-icon-chat' />
                                                <span>Скопировано</span>
                                              </>
                                            ) : (
                                              <>
                                                <CopyIcon className='copy-icon-chat' />
                                                <span>Копировать</span>
                                              </>
                                            )}
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex items-center justify-center py-2">
                                          <LoadingIcon width={32} height={32} />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </article>
                  ))}

                  <article key={'99999999'}>
                    <div ref={endOfListRef} />
                  </article>
                </div>
              </div>
            </>
          )}
          </div>
        </div>
      </div>
      
      {/* Input area - centered and with safe-area support */}
      <div className='bg-white border-t border-gray-100 flex-shrink-0'>
        <div className="w-full flex justify-center px-4 py-6">
          <div className="max-w-4xl w-full mx-auto">
            <div className="relative">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gray-800 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  AI
                </div>
                {messages.length > 0 && messages[messages.length - 1]?.is_loading && !messages[messages.length - 1]?.is_request && (
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                )}
                <div id="composer-background"
                  className="flex w-full cursor-text flex-col rounded-2xl px-4 py-3 transition-colors bg-gray-100 border border-gray-200 shadow-sm flex-1">
                  <div className="relative flex min-h-[40px] items-center">
                    <div className="min-w-0 max-w-full flex-1 pr-12">
                      <div className="text-gray-700 max-h-32 overflow-auto">
                        <div 
                          contentEditable="true" 
                          translate="no" 
                          className="outline-none resize-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400" 
                          id="prompt-textarea" 
                          data-virtualkeyboard="true" 
                          ref={editorRef} 
                          tabIndex={0} 
                          onPaste={handlePaste}
                          onInput={handleInput}
                          onKeyPress={handleKeyPress}
                          onClick={handleFocus}
                          data-placeholder="Написать AI асистенту"
                        >
                        </div>
                      </div>
                    </div>
                    <div className="absolute right-2 bottom-2">
                      <button
                        onClick={handleSendClick}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                        aria-label="Отправить сообщение"
                      >
                        <SendIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#9B9BA4', textAlign: 'center' }} className='mt-2'>
                OurGold иногда делает ошибки.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
